import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {number | null} */
  #dragStartY = null;
  #dragCurrentY = 0;
  #dragHeight = 1;
  /** @type {AbortController | null} */
  #dragAbort = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('pointerdown', this.#onDragStart);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('pointerdown', this.#onDragStart);
    this.#dragAbort?.abort();
  }

  /**
   * Begins a swipe-to-close gesture from the drawer's grab handle.
   * @param {PointerEvent} event
   */
  #onDragStart = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!this.isOpen) return;

    const target = event.target;
    if (!(target instanceof Element) || !target.closest('.menu-drawer__handle')) return;
    // Let the logo link and close button receive their taps
    if (target.closest('.menu-drawer__logo, .menu-drawer__close-button')) return;

    const drawer = this.refs.menuDrawer;
    this.#dragStartY = event.clientY;
    this.#dragCurrentY = 0;
    this.#dragHeight = drawer.getBoundingClientRect().height || 1;
    drawer.style.transition = 'none';

    this.#dragAbort = new AbortController();
    const { signal } = this.#dragAbort;
    window.addEventListener('pointermove', this.#onDragMove, { signal });
    window.addEventListener('pointerup', this.#onDragEnd, { signal });
    window.addEventListener('pointercancel', this.#onDragEnd, { signal });
  };

  /** @param {PointerEvent} event */
  #onDragMove = (event) => {
    if (this.#dragStartY == null) return;
    this.#dragCurrentY = Math.max(0, event.clientY - this.#dragStartY);
    this.refs.menuDrawer.style.transform = `translateY(${this.#dragCurrentY}px)`;
  };

  #onDragEnd = () => {
    const drawer = this.refs.menuDrawer;
    const wasDragging = this.#dragStartY != null;

    this.#dragAbort?.abort();
    this.#dragAbort = null;
    this.#dragStartY = null;

    if (!wasDragging) return;

    const dragged = this.#dragCurrentY;
    const threshold = Math.min(140, this.#dragHeight * 0.28);

    if (dragged > threshold) {
      // Glide the rest of the way down, then close and clean up.
      drawer.style.transition = 'transform 0.34s cubic-bezier(0.22, 1, 0.36, 1)';
      drawer.style.transform = `translateY(${this.#dragHeight}px)`;
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        this.close();
        drawer.style.transition = '';
        drawer.style.transform = '';
      };
      drawer.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 380);
    } else {
      // Snap back into place.
      drawer.style.transition = 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)';
      drawer.style.transform = 'translateY(0)';
      let settled = false;
      const reset = () => {
        if (settled) return;
        settled = true;
        drawer.style.transition = '';
        drawer.style.transform = '';
      };
      drawer.addEventListener('transitionend', reset, { once: true });
      setTimeout(reset, 360);
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      // Clean up any leftover closing class from an interrupted close
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      if (drawer instanceof HTMLElement) {
        drawer.classList.remove('menu-drawer--closing');
      }

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer's open animation to complete before trapping focus.
      // Use animationend (not getAnimations) so it fires reliably after the
      // keyframe animation that .menu-open triggers.
      if (drawer instanceof HTMLElement) {
        const trap = () => {
          drawer.removeEventListener('animationend', trap);
          trapFocus(details);
        };
        drawer.addEventListener('animationend', trap);
        // Fallback
        setTimeout(() => {
          drawer.removeEventListener('animationend', trap);
          trapFocus(details);
        }, 650);
      } else {
        trapFocus(details);
      }
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    const isMainDrawer = details === this.refs.details;

    // If the drawer was dragged closed, it has inline transform/transition
    // from the drag gesture — it's already visually down. Skip the close
    // animation and clean up directly.
    if (drawer instanceof HTMLElement && drawer.style.transform !== '') {
      drawer.style.transition = '';
      drawer.style.transform = '';
      drawer.classList.remove('menu-drawer--closing');
      details.classList.remove('menu-open');
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
      reset(details);
      if (isMainDrawer) {
        removeTrapFocus();
        this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
      return;
    }

    // Submenu close: keep the transition approach (horizontal slide)
    if (!isMainDrawer) {
      details.classList.remove('menu-open');
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

      if (drawer instanceof HTMLElement) {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          drawer.removeEventListener('transitionend', onTransitionEnd);
          reset(details);
          trapFocus(this.refs.details);
        };
        const onTransitionEnd = (event) => {
          if (event.target !== drawer) return;
          finish();
        };
        drawer.addEventListener('transitionend', onTransitionEnd);
        setTimeout(finish, 600);
      } else {
        reset(details);
        trapFocus(this.refs.details);
      }
      return;
    }

    // Main drawer close: play the slide-down keyframe animation, then clean
    // up. Mirrors dialog.js: add a --closing class, wait for animationend,
    // then remove [open]. .menu-open is kept on the parent so visibility
    // stays visible through the whole slide (no mid-slide flicker).
    if (drawer instanceof HTMLElement) {
      drawer.classList.add('menu-drawer--closing');
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        drawer.removeEventListener('animationend', onAnimationEnd);
        drawer.classList.remove('menu-drawer--closing');
        details.classList.remove('menu-open');
        this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
        reset(details);
        removeTrapFocus();
        this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
      };
      const onAnimationEnd = (event) => {
        if (event.target !== drawer) return;
        finish();
      };
      drawer.addEventListener('animationend', onAnimationEnd);
      // Fallback if animationend doesn't fire (e.g. interrupted)
      setTimeout(finish, 550);
    } else {
      details.classList.remove('menu-open');
      reset(details);
      removeTrapFocus();
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
